import { css } from 'twin.macro';
import React, { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { AdminTable } from '../../../../../Admin/reusableStyles';
import LoadingSpiner from '../../../../../../_elements/LoadingSpiner';
import { fetchWrapper, getChannelIdFromSettingsURL, getTeamIdFromURL } from '../../../../../../_helpers';
import { CertificateExpiryBadge } from '../Common/CertificateExpiryBadge';
import { CertificateActionButtons } from '../Common/CertificateActionButtons';
import { UserInfo } from '../Common/CourseMetricContainer';

const SubmittedCertificates = ({ users }) => {
    const [certificates, setCertificates] = useState([]);
    const [gridData, setGridData] = useState([]);
    const [loading, setLoading] = useState(false);
    const team = getTeamIdFromURL();
    const chatroom = getChannelIdFromSettingsURL();

    const fetchAllCertificates = useCallback(async () => {
        if (!users?.length) return;
        setLoading(true);
        try {
            const query = team ? `?team=${team}` : chatroom ? `?chatroom=${chatroom}` : '';
            const response = await fetchWrapper.get(`user-certificates${query}`);
            setCertificates(response || []);
        } catch (error) {
            console.error('Failed to fetch certificates:', error);
        } finally {
            setLoading(false);
        }
    }, [team, chatroom, users]);

    useEffect(() => {
        fetchAllCertificates();
    }, [fetchAllCertificates]);

    useEffect(() => {
        if (!certificates || !users) {
            setGridData([]);
            return;
        }
        // Map certificates to grid rows with user info
        const userMap = {};
        (users || []).forEach((u) => {
            userMap[u._id || u.id] = u;
        });

        const rows = (certificates || []).map((cert) => {
            const user = userMap[cert.user?._id || cert.user] || cert.user || {};
            return {
                certId: cert._id,
                user,
                userEmail: user.email || cert.user?.email || '—',
                certificateType: cert.certificateType || '—',
                courseName: cert.courseName || '—',
                courseTakenAt: cert.courseTakenAt,
                courseExpiresAt: cert.courseExpiresAt,
                courseRenewalLink: cert.courseRenewalLink,
                form: cert.form,
                status: cert.status,
            };
        });

        // Sort: expiring soonest first, expired first
        rows.sort((a, b) => {
            if (!a.courseExpiresAt && !b.courseExpiresAt) return 0;
            if (!a.courseExpiresAt) return 1;
            if (!b.courseExpiresAt) return -1;
            return new Date(a.courseExpiresAt) - new Date(b.courseExpiresAt);
        });

        setGridData(rows);
    }, [certificates, users]);

    if (loading) {
        return <LoadingSpiner size={42} />;
    }

    if (!gridData.length) {
        return (
            <div
                css={css`
                    padding: 1rem 2rem;
                `}
            >
                <div className="custom-card" style={{ padding: '2rem', textAlign: 'center' }}>
                    No certificates found
                </div>
            </div>
        );
    }

    return (
        <div
            css={css`
                padding: 1rem 2rem;
            `}
        >
            <div className="custom-card card-table">
                <AdminTable className="w-100 text-dark-grey">
                    <thead>
                        <tr>
                            <th className="text-left">User</th>
                            <th className="text-left">Cert Type</th>
                            <th className="text-left">Course Name</th>
                            <th className="text-left">Date Taken</th>
                            <th className="text-left">Expiration</th>
                            <th className="text-center">Status</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-light-grey">
                        {gridData.map((row, idx) => (
                            <tr key={row.certId || idx}>
                                <td className="text-left">{<UserInfo user={row?.user} />}</td>
                                <td className="text-left">
                                    <span style={{ fontWeight: 600 }}>{row.certificateType}</span>
                                </td>
                                <td className="text-left">{row.courseName}</td>
                                <td className="text-left">
                                    {row.courseTakenAt ? dayjs(row.courseTakenAt).format('MMM D, YYYY') : '—'}
                                </td>
                                <td className="text-left">
                                    {row.courseExpiresAt ? dayjs(row.courseExpiresAt).format('MMM D, YYYY') : '—'}
                                </td>
                                <td className="text-center">
                                    {row.courseExpiresAt ? (
                                        <CertificateExpiryBadge courseExpiresAt={row.courseExpiresAt} />
                                    ) : (
                                        <span className="badge badge-success" style={{ fontSize: '1rem' }}>
                                            {row.status || 'active'}
                                        </span>
                                    )}
                                </td>
                                <td className="text-center">
                                    <CertificateActionButtons
                                        formId={row.form}
                                        courseRenewalLink={row.courseRenewalLink}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </AdminTable>
            </div>
        </div>
    );
};

export default SubmittedCertificates;
